<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import DealDrawerContent from 'dashboard/routes/dashboard/kanban/DealDrawerContent.vue';
import AddNegotiationModal from 'dashboard/routes/dashboard/kanban/AddNegotiationModal.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const store = useStore();

// --- UI state ---
const targetDealStatus = ref(null);
const selectedLostReason = ref('');
const selectedPaymentMethod = ref('');
const showCustomOriginInput = ref(false);
const customOriginValue = ref('');
const editingClosedAt = ref(false);

// --- conversation-derived ---
const customAttributes = computed(
  () => props.conversation?.custom_attributes || {}
);
const conversationId = computed(() => props.conversation?.id);
const dealValue = computed(() => customAttributes.value.deal_value || '');
const dealStatus = computed(() => customAttributes.value.deal_status || null);
const dealClosedAt = computed(
  () => customAttributes.value.deal_closed_at || null
);
const dealClosedAtDate = computed(() => {
  const iso = dealClosedAt.value;
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
});
const dealOrigin = computed(() => customAttributes.value.deal_origin || '');
const dealInstallments = computed(
  () => customAttributes.value.deal_installments || []
);
const dealPaidValue = computed(() => {
  // Com parcelas (geradas no Kanban), o valor pago é derivado da soma das
  // pagas; sem parcelas, vale o campo manual legado.
  if (dealInstallments.value.length) {
    return dealInstallments.value.reduce(
      (acc, p) => acc + (p.paid ? parseFloat(p.value) || 0 : 0),
      0
    );
  }
  return parseFloat(customAttributes.value.deal_paid_value) || 0;
});
const dealReceivable = computed(() => {
  const total = parseFloat(customAttributes.value.deal_value) || 0;
  return Math.max(0, total - dealPaidValue.value);
});
const formattedDealReceivable = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(dealReceivable.value)
);
const leadSource = computed(() => {
  const ca = customAttributes.value;
  if (!ca || !ca.lead_source_ad_name) return null;
  return {
    platform: ca.lead_source_platform,
    ad_name: ca.lead_source_ad_name,
    ad_body: ca.lead_source_ad_body,
    ad_id: ca.lead_source_ad_id,
    source_url: ca.lead_source_source_url,
    media_url: ca.lead_source_media_url,
    thumbnail_url: ca.lead_source_thumbnail_url,
  };
});

// --- account-derived config ---
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](store.getters.getCurrentAccountId)
);

// Em modo deals a verdade é o Crm::Deal, não os custom_attributes. Editar por
// aqui os atributos antigos criaria um valor paralelo ao que o quadro e os
// relatórios mostram, divergindo em silêncio. Então a aba passa a ser o mesmo
// editor do drawer do Kanban, apontando pro negócio desta conversa.
const isDealsMode = computed(() => currentAccount.value?.crm_mode === 'deals');
const dealContactId = computed(() => props.conversation?.meta?.sender?.id);
const contactDeals = ref([]);
const selectedDealId = ref(null);
const isLoadingDeal = ref(false);
const showNewDealModal = ref(false);

// O negócio pertence ao CONTATO; a conversa é só quem o originou. Listar por
// contato faz o mesmo cliente com três projetos aparecer inteiro aqui, e traz
// junto os negócios criados pelo quadro (que nascem sem conversa e antes
// sumiam do atendimento).
const loadContactDeals = async () => {
  if (!isDealsMode.value || !dealContactId.value) {
    contactDeals.value = [];
    selectedDealId.value = null;
    return;
  }
  isLoadingDeal.value = true;
  try {
    const deals = await store.dispatch(
      'crmDeals/fetchDealsByContact',
      dealContactId.value
    );
    contactDeals.value = deals;
    // Abre no negócio desta conversa; sem ele, no mais recente. O payload da
    // conversa expõe display_id como `id`, daí a comparação com
    // conversation_display_id e não com a chave da tabela.
    const daConversa = deals.find(
      d => String(d.conversation_display_id) === String(conversationId.value)
    );
    selectedDealId.value = daConversa?.id || deals[0]?.id || null;
  } catch {
    contactDeals.value = [];
    selectedDealId.value = null;
  } finally {
    isLoadingDeal.value = false;
  }
};

const isFromThisConversation = deal =>
  String(deal.conversation_display_id) === String(conversationId.value);

const dealLabel = deal => {
  const titulo = deal.title || 'Sem título';
  const valor = parseFloat(deal.value);
  const valorFmt = Number.isNaN(valor)
    ? ''
    : ` · ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)}`;
  return `${titulo}${valorFmt}`;
};

const onDealDeleted = async () => {
  selectedDealId.value = null;
  await loadContactDeals();
};

const onDealCreated = async () => {
  showNewDealModal.value = false;
  const antes = contactDeals.value.map(d => d.id);
  await loadContactDeals();
  const novo = contactDeals.value.find(d => !antes.includes(d.id));
  if (novo) selectedDealId.value = novo.id;
};

watch([dealContactId, isDealsMode], loadContactDeals, { immediate: true });
const accountAttributes = computed(
  () => currentAccount.value?.custom_attributes || {}
);
const isCurrentUserAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const wonLabel = computed(
  () => accountAttributes.value.crm_won_label || 'ganho'
);
const lostLabel = computed(
  () => accountAttributes.value.crm_lost_label || 'perdido'
);
const crmOrigins = computed(() => {
  const items = accountAttributes.value.crm_origins;
  return items && items.length > 0
    ? items
    : ['WhatsApp', 'Instagram', 'Google Ads', 'Indicação', 'Website'];
});
const crmPaymentMethods = computed(() => {
  const items = accountAttributes.value.crm_payment_methods;
  return items && items.length > 0
    ? items
    : ['Pix', 'Cartão de Crédito', 'Boleto', 'Transferência Bancária'];
});
const crmLostReasons = computed(() => {
  const items = accountAttributes.value.crm_lost_reasons;
  return items && items.length > 0
    ? items
    : [
        'Preço',
        'Sem interesse',
        'Fechou com concorrente',
        'Parou de responder',
        'Outros',
      ];
});

// --- mutations (dispatch by conversation id; store reactivity refreshes the UI) ---
const persistCustomAttributes = attrs => {
  if (!conversationId.value) return;
  store.dispatch('updateCustomAttributes', {
    conversationId: conversationId.value,
    customAttributes: attrs,
  });
};

const updateDealValue = event =>
  persistCustomAttributes({ deal_value: event.target.value });

const updateDealPaidValue = event => {
  if (dealInstallments.value.length) return; // derivado das parcelas
  persistCustomAttributes({ deal_paid_value: event.target.value });
};

const updateDealOrigin = val => persistCustomAttributes({ deal_origin: val });

const handleOriginSelect = event => {
  const val = event.target.value;
  if (val === '__custom__') {
    showCustomOriginInput.value = true;
    customOriginValue.value = '';
    return;
  }
  updateDealOrigin(val);
};

const saveCustomOrigin = () => {
  if (customOriginValue.value.trim()) {
    updateDealOrigin(customOriginValue.value.trim());
  }
  showCustomOriginInput.value = false;
  customOriginValue.value = '';
};

const updateDealClosedAt = event => {
  if (!isCurrentUserAdmin.value) return;
  const dateStr = event.target.value;
  if (!dateStr) return;
  const existing = dealClosedAt.value
    ? new Date(dealClosedAt.value)
    : new Date();
  const [y, m, d] = dateStr.split('-').map(Number);
  const next = new Date(existing);
  next.setFullYear(y, m - 1, d);
  persistCustomAttributes({ deal_closed_at: next.toISOString() });
  editingClosedAt.value = false;
};

const formatClosedAt = isoStr => {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const setDealStatus = status => {
  // Toggling off the current status
  if (dealStatus.value === status) {
    confirmDealStatusChange(null);
    targetDealStatus.value = null;
    return;
  }
  targetDealStatus.value = status;
  selectedLostReason.value = customAttributes.value.deal_lost_reason || '';
  selectedPaymentMethod.value =
    customAttributes.value.deal_payment_method || '';
};

const cancelDealStatusUpdate = () => {
  targetDealStatus.value = null;
};

// Aplica etiqueta won/lost na conversa e no contato, e adia a conversa
// até a próxima resposta (mesma regra do Kanban: marcar ganho/perdido NÃO
// resolve; adia e reabre a mesma conversa quando o lead voltar).
const applyDealOutcomeSideEffects = async status => {
  const dealLabels = [wonLabel.value, lostLabel.value];
  const targetLabel =
    status === 'won'
      ? wonLabel.value
      : status === 'lost'
        ? lostLabel.value
        : null;
  try {
    const convLabels = [...(props.conversation.labels || [])].filter(
      l => !dealLabels.includes(l)
    );
    if (targetLabel) convLabels.push(targetLabel);
    await store.dispatch('conversationLabels/update', {
      conversationId: conversationId.value,
      labels: convLabels,
    });

    const contactId = props.conversation?.meta?.sender?.id;
    if (contactId) {
      await store.dispatch('contactLabels/get', contactId);
      const current =
        store.getters['contactLabels/getContactLabels'](contactId) || [];
      const contactLabels = [...current].filter(l => !dealLabels.includes(l));
      if (targetLabel) contactLabels.push(targetLabel);
      await store.dispatch('contactLabels/update', {
        contactId,
        labels: contactLabels,
      });
    }

    // Adia até a próxima resposta (snoozedUntil null) ao marcar; desmarcar não mexe.
    if (status) {
      await store.dispatch('toggleStatus', {
        conversationId: conversationId.value,
        status: 'snoozed',
        snoozedUntil: null,
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      '[ConversationDealSection] aplicar resultado do negócio falhou:',
      e
    );
  }
};

const confirmDealStatusChange = status => {
  const existingClosedAt = customAttributes.value.deal_closed_at;
  const closedAt = !status
    ? existingClosedAt
    : existingClosedAt || new Date().toISOString();

  const attrs = { deal_status: status, deal_closed_at: closedAt };
  if (status === 'lost') {
    attrs.deal_lost_reason = selectedLostReason.value;
  } else if (status === 'won') {
    attrs.deal_payment_method = selectedPaymentMethod.value;
    // Com parcelas existentes o valor pago é derivado — não sobrescrever.
    if (!dealInstallments.value.length) {
      attrs.deal_paid_value = customAttributes.value.deal_value || '0';
    }
  }

  persistCustomAttributes(attrs);
  applyDealOutcomeSideEffects(status);
  targetDealStatus.value = null;
};
</script>

<template>
  <!-- Conta em modo deals: o editor é o mesmo do drawer do Kanban, apontando
       pro negócio desta conversa. Assim funil, etapa, valor, status, parcelas e
       tarefas são os do negócio de verdade, e não uma cópia paralela. -->
  <div v-if="isDealsMode" class="flex flex-col">
    <div v-if="isLoadingDeal" class="flex items-center justify-center py-6">
      <span class="i-lucide-loader-2 w-5 h-5 animate-spin text-n-slate-9" />
    </div>

    <template v-else>
      <!-- Seletor só quando há mais de um: com um negócio só ele seria ruído. -->
      <div v-if="contactDeals.length > 1" class="px-2 pt-2">
        <label
          class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
          >Negociação</label
        >
        <select
          v-model="selectedDealId"
          class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
        >
          <option v-for="d in contactDeals" :key="d.id" :value="d.id">
            {{ isFromThisConversation(d) ? '• ' : '' }}{{ dealLabel(d) }}
          </option>
        </select>
        <p class="text-xs text-n-slate-10 mt-1 mb-0">
          {{ contactDeals.length }} negociações deste contato · • marca a desta
          conversa
        </p>
      </div>

      <DealDrawerContent
        v-if="selectedDealId"
        :key="selectedDealId"
        :deal-id="selectedDealId"
        embedded
        @deleted="onDealDeleted"
      />
      <p v-else class="text-sm text-n-slate-11 px-2 pt-4 pb-2 m-0">
        Este contato ainda não tem negócio.
      </p>

      <div class="px-2 pb-3 pt-1">
        <button
          class="w-full px-3 py-2 text-sm font-semibold text-n-brand bg-n-brand/10 hover:bg-n-brand/20 rounded-lg transition-colors cursor-pointer border-none"
          @click="showNewDealModal = true"
        >
          + Novo negócio
        </button>
      </div>
    </template>

    <AddNegotiationModal
      :show="showNewDealModal"
      is-deals-mode
      :preset-contact="conversation.meta?.sender"
      @close="showNewDealModal = false"
      @negotiation-added="onDealCreated"
    />
  </div>

  <div v-else class="flex flex-col gap-3">
    <!-- Valor + Origem -->
    <div class="flex items-center gap-3">
      <div class="flex-1">
        <label
          class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
          >Valor do Negócio</label
        >
        <div class="flex items-center gap-1">
          <span class="text-xs font-bold text-n-blue-11">R$</span>
          <input
            :value="dealValue"
            type="number"
            step="0.01"
            placeholder="0,00"
            class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand font-semibold"
            @change="updateDealValue($event)"
          />
        </div>
      </div>
      <div class="flex-1">
        <label
          class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
          >Origem</label
        >
        <select
          v-if="!showCustomOriginInput"
          :value="dealOrigin"
          class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
          @change="handleOriginSelect($event)"
        >
          <option value="" disabled>Selecionar...</option>
          <option v-for="o in crmOrigins" :key="o" :value="o">{{ o }}</option>
          <option value="__custom__">✏️ Outro...</option>
        </select>
        <div v-else class="flex items-center gap-1">
          <input
            v-model="customOriginValue"
            type="text"
            placeholder="Digite a origem..."
            class="flex-1 px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand"
            @keyup.enter="saveCustomOrigin"
          />
          <button
            class="px-2 py-1 text-xs rounded bg-woot-500 text-white border-0 cursor-pointer hover:bg-woot-600"
            @click="saveCustomOrigin"
          >
            OK
          </button>
          <button
            class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11 border border-n-weak cursor-pointer"
            @click="showCustomOriginInput = false"
          >
            ✕
          </button>
        </div>
      </div>
    </div>

    <!-- Botões Ganho/Perda -->
    <div v-if="!targetDealStatus" class="flex gap-2">
      <button
        class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer border-0"
        :class="
          dealStatus === 'won'
            ? 'bg-woot-500 text-white shadow-md'
            : 'bg-n-blue-3 text-n-blue-11 outline outline-1 outline-n-blue-6 hover:bg-n-blue-4'
        "
        @click="setDealStatus('won')"
      >
        🏆 Ganho
      </button>
      <button
        class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer border-0"
        :class="
          dealStatus === 'lost'
            ? 'bg-n-ruby-9 text-white shadow-md'
            : 'bg-n-ruby-3 text-n-ruby-11 outline outline-1 outline-n-ruby-6 hover:bg-n-ruby-4'
        "
        @click="setDealStatus('lost')"
      >
        ❌ Perdido
      </button>
    </div>

    <!-- Confirmação (forma de pagamento / motivo da perda) -->
    <div
      v-else
      class="p-3 bg-white dark:bg-slate-800 border border-n-weak rounded-lg shadow-sm"
    >
      <label class="block text-xs font-semibold text-n-slate-11 mb-2">
        {{
          targetDealStatus === 'won' ? 'Forma de Pagamento' : 'Motivo da Perda'
        }}
      </label>
      <select
        v-if="targetDealStatus === 'won'"
        v-model="selectedPaymentMethod"
        class="w-full px-3 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1 mb-3"
      >
        <option value="" disabled>Selecione a forma de pagamento</option>
        <option
          v-for="method in crmPaymentMethods"
          :key="method"
          :value="method"
        >
          {{ method }}
        </option>
      </select>
      <select
        v-if="targetDealStatus === 'lost'"
        v-model="selectedLostReason"
        class="w-full px-3 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1 mb-3"
      >
        <option value="" disabled>Selecione o motivo da perda</option>
        <option v-for="reason in crmLostReasons" :key="reason" :value="reason">
          {{ reason }}
        </option>
      </select>
      <div class="flex items-center gap-2">
        <woot-button
          size="small"
          variant="clear"
          class="flex-1"
          @click="cancelDealStatusUpdate"
        >
          Cancelar
        </woot-button>
        <woot-button
          size="small"
          :color-scheme="targetDealStatus === 'won' ? 'success' : 'alert'"
          class="flex-1"
          :is-disabled="
            targetDealStatus === 'won'
              ? !selectedPaymentMethod
              : !selectedLostReason
          "
          @click="confirmDealStatusChange(targetDealStatus)"
        >
          Confirmar
        </woot-button>
      </div>
    </div>

    <!-- Data de fechamento + detalhes -->
    <p v-if="dealClosedAt" class="text-xs text-n-slate-11 m-0">
      <span v-if="!editingClosedAt">
        {{ dealStatus === 'won' ? 'Ganho' : 'Perdido' }} em
        {{ formatClosedAt(dealClosedAt) }}
        <button
          v-if="isCurrentUserAdmin"
          type="button"
          class="ml-2 text-n-brand hover:underline"
          @click="editingClosedAt = true"
        >
          editar
        </button>
      </span>
      <span v-else class="inline-flex items-center gap-2">
        Data:
        <input
          type="date"
          :value="dealClosedAtDate"
          class="px-2 py-1 rounded-md bg-n-background border border-n-weak text-n-slate-12"
          @change="updateDealClosedAt"
        />
        <button
          type="button"
          class="text-n-slate-11 hover:underline"
          @click="editingClosedAt = false"
        >
          cancelar
        </button>
      </span>
      <br />
      <span
        v-if="dealStatus === 'won' && customAttributes.deal_payment_method"
        class="font-medium"
      >
        Pagamento: {{ customAttributes.deal_payment_method }}
      </span>
      <span
        v-if="dealStatus === 'lost' && customAttributes.deal_lost_reason"
        class="font-medium text-n-ruby-11"
      >
        Motivo: {{ customAttributes.deal_lost_reason }}
      </span>
    </p>

    <!-- Valor Pago / A Receber (apenas ganho) -->
    <div
      v-if="dealStatus === 'won'"
      class="p-3 bg-n-surface-1 border border-n-weak rounded-lg"
    >
      <div class="flex items-center gap-3 mb-2">
        <div class="flex-1">
          <label class="block text-xs font-semibold text-n-slate-11 mb-1"
            >Valor Pago</label
          >
          <div class="flex items-center gap-1">
            <span class="text-xs font-bold text-n-slate-11">R$</span>
            <input
              :value="dealPaidValue"
              type="number"
              step="0.01"
              min="0"
              placeholder="0,00"
              :readonly="dealInstallments.length > 0"
              class="w-full px-2 py-1.5 rounded-md text-sm bg-n-background border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand font-semibold"
              :class="{
                'opacity-60 cursor-not-allowed': dealInstallments.length > 0,
              }"
              @change="updateDealPaidValue($event)"
            />
          </div>
        </div>
        <div class="flex-1">
          <label class="block text-xs font-semibold text-n-slate-11 mb-1"
            >A Receber</label
          >
          <div
            class="px-2 py-1.5 rounded-md text-sm font-bold"
            :class="
              dealReceivable > 0
                ? 'text-amber-500 bg-amber-500/10'
                : 'text-emerald-500 bg-emerald-500/10'
            "
          >
            {{ formattedDealReceivable }}
          </div>
        </div>
      </div>
      <div
        v-if="dealReceivable <= 0 && dealPaidValue > 0"
        class="text-xs text-emerald-500 font-medium flex items-center gap-1"
      >
        ✅ Pagamento completo
      </div>
    </div>
  </div>

  <!-- Origem do Lead (anúncio) -->
  <div
    v-if="leadSource"
    class="p-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20"
  >
    <div class="flex items-center gap-2 mb-2">
      <span class="text-sm">📣</span>
      <span
        class="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide"
        >Origem do Lead</span
      >
      <span
        v-if="leadSource.platform"
        class="ml-auto inline-flex items-center text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 px-2 py-0.5 rounded-full"
      >
        {{ leadSource.platform }}
      </span>
    </div>
    <p
      v-if="leadSource.ad_name"
      class="text-sm font-semibold text-n-slate-12 m-0 mb-1"
    >
      {{ leadSource.ad_name }}
    </p>
    <p
      v-if="leadSource.ad_body"
      class="text-xs text-n-slate-11 m-0 mb-2"
      style="
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      "
    >
      {{ leadSource.ad_body }}
    </p>
    <div class="flex items-center gap-2">
      <img
        v-if="leadSource.thumbnail_url || leadSource.media_url"
        :src="leadSource.thumbnail_url || leadSource.media_url"
        class="w-10 h-10 rounded object-cover border border-n-weak"
        @error="$event.target.style.display = 'none'"
      />
      <div class="flex-1 min-w-0">
        <a
          v-if="leadSource.source_url"
          :href="leadSource.source_url"
          target="_blank"
          class="text-xs text-violet-600 dark:text-violet-400 hover:underline truncate block"
        >
          🔗 Ver anúncio original
        </a>
        <span
          v-if="leadSource.ad_id"
          class="text-xs text-n-slate-10 block truncate"
        >
          ID: {{ leadSource.ad_id }}
        </span>
      </div>
    </div>
  </div>
</template>
